import React, { Suspense } from 'react';
import { Switch, Route } from 'react-router-dom';
import RestrictedRoute from './RestrictedRoute';

// Auth/layout — eager imports (must load immediately)
import LoginPage from '../scenes/login/pages/LoginPage';
import WelcomePage from '../scenes/login/pages/WelcomePage';
import ForgotPasswordPage from '../scenes/login/pages/ForgotPasswordPage';
import Forbidden from '../scenes/misc/pages/Forbidden';
import { ROLES } from '../utils/constants';

// Lazy-loaded page components
const HomePage = React.lazy(() => import('../scenes/home/pages/HomePage'));

const BrandsList = React.lazy(() => import('../scenes/products/brands/pages/BrandsList'));
const BrandEdit = React.lazy(() => import('../scenes/products/brands/pages/BrandEdit'));
const BrandCreate = React.lazy(() => import('../scenes/products/brands/pages/BrandCreate'));

const IngredientsList = React.lazy(() => import('../scenes/products/ingredients/pages/IngredientsList'));
const IngredientEdit = React.lazy(() => import('../scenes/products/ingredients/pages/IngredientEdit'));
const IngredientCreate = React.lazy(() => import('../scenes/products/ingredients/pages/IngredientCreate'));

const HsCodesList = React.lazy(() => import('../scenes/products/hscodes/pages/HsCodesList'));
const HsCodeEdit = React.lazy(() => import('../scenes/products/hscodes/pages/HsCodeEdit'));
const HsCodeCreate = React.lazy(() => import('../scenes/products/hscodes/pages/HsCodeCreate'));

const CategoriesList = React.lazy(() => import('../scenes/products/categories/pages/CategoriesList'));
const CategoryEdit = React.lazy(() => import('../scenes/products/categories/pages/CategoryEdit'));
const CategoryCreate = React.lazy(() => import('../scenes/products/categories/pages/CategoryCreate'));

const GenericProductsList = React.lazy(() => import('../scenes/products/genericProducts/pages/GenericProductsList'));
const GenericProductEdit = React.lazy(() => import('../scenes/products/genericProducts/pages/GenericProductEdit'));

const ManufacturersList = React.lazy(() => import('../scenes/contractors/manufacturers/pages/ManufacturersList'));
const ManufacturerEdit = React.lazy(() => import('../scenes/contractors/manufacturers/pages/ManufacturerEdit'));

const SuppliersList = React.lazy(() => import('../scenes/contractors/suppliers/pages/SuppliersList'));
const SupplierEdit = React.lazy(() => import('../scenes/contractors/suppliers/pages/SupplierEdit'));

const CustomersList = React.lazy(() => import('../scenes/contractors/customers/pages/CustomersList'));
const CustomerEdit = React.lazy(() => import('../scenes/contractors/customers/pages/CustomerEdit'));

const PalletsList = React.lazy(() => import('../scenes/products/pallets/pages/PalletsList'));
const PalletEdit = React.lazy(() => import('../scenes/products/pallets/pages/PalletEdit'));

const LabelsList = React.lazy(() => import('../scenes/products/labels/pages/LabelsList'));
const LabelEdit = React.lazy(() => import('../scenes/products/labels/pages/LabelEdit'));

const ContainersList = React.lazy(() => import('../scenes/products/containers/pages/ContainersList'));
const ContainerEdit = React.lazy(() => import('../scenes/products/containers/pages/ContainerEdit'));

const TrucksList = React.lazy(() => import('../scenes/products/trucks/pages/TrucksList'));
const TruckEdit = React.lazy(() => import('../scenes/products/trucks/pages/TruckEdit'));

const PackagesList = React.lazy(() => import('../scenes/products/packages/pages/PackagesList'));
const PackageEdit = React.lazy(() => import('../scenes/products/packages/pages/PackageEdit'));

const UserEdit = React.lazy(() => import('../scenes/users/pages/UserEdit'));

const OrdersList = React.lazy(() => import('../scenes/orders/orders/pages/OrdersList'));
const OrderEdit = React.lazy(() => import('../scenes/orders/orders/pages/OrderEdit'));
const OrderProductSelection = React.lazy(() => import('../scenes/orders/orders/pages/OrderProductSelection'));
const OrderProductsCalculations = React.lazy(() => import('../scenes/orders/orders/pages/ProductsCalculations'));
const OrderPrintout = React.lazy(() => import('../scenes/orders/orders/pages/OrderPrintout'));
const PalletDescription = React.lazy(() => import('../scenes/orders/orders/pages/palletDescription/PalletDescriptions'));
const PalletNumbering = React.lazy(() => import('../scenes/orders/orders/pages/palletDescription/PalletNumbering'));
const OrderReserve = React.lazy(() => import('../scenes/orders/orders/pages/orderReserve/OrderReserve'));
const Status = React.lazy(() => import('../scenes/orders/orders/pages/Status/StatusDashboard'));
const Proforma = React.lazy(() => import('../scenes/orders/orders/pages/Proforma/Proforma'));
const FnbOrdersList = React.lazy(() => import('../scenes/orders/orders/pages/FnbOrdersList'));
const FnbOrder = React.lazy(() => import('../scenes/orders/orders/pages/FnbOrder'));

const DataOperations = React.lazy(() => import('../scenes/products/imports/pages/DataOperations'));
const UploadForImport = React.lazy(() => import('../scenes/products/imports/pages/UploadForImport'));
const UploadForImportSizes = React.lazy(() => import('../scenes/products/imports/pages/UploadForImportSizes'));
const MatchChocolateToGtin = React.lazy(() => import('../scenes/products/imports/pages/MatchChocolateToGtin'));
const ProductPackingAnalysis = React.lazy(() => import('../scenes/products/imports/pages/ProductPackingAnalysis'));

const WarehousesList = React.lazy(() => import('../scenes/warehouses/warehouses/pages/WarehousesList'));
const WarehouseEdit = React.lazy(() => import('../scenes/warehouses/warehouses/pages/WarehouseEdit'));
const DisplayMatching = React.lazy(() => import('../scenes/products/imports/pages/DisplayMatching'));
const UploadForImportSupplierData = React.lazy(() => import('../scenes/products/imports/pages/UploadForImportSupplierData'));
const TransportPricingEdit = React.lazy(() => import('../scenes/other/TransportPricing/pages/TransportPricingEdit'));
const TransportPricingList = React.lazy(() => import('../scenes/other/TransportPricing/pages/TransportPricingList'));
const Invoice = React.lazy(() => import('../scenes/orders/orders/pages/Invoice'));

const InventoryMove = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryMove'));
const InventoryAddSelectProduct = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryAddSelectProduct'));
const InventoryEdit = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryEdit'));
const InventoryAdd = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryAdd'));
const InventoryAddFromTemplate = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryAddFromTemplate'));
const InventoryAdjustment = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryAdjustment'));
const InventoryForGeneric = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryForGeneric'));
const InventorySplit = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventorySplit'));
const InventoryRemove = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryRemove'));
const InventoryRemoveEdit = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryRemoveEdit'));
const Inventory = React.lazy(() => import('../scenes/warehouses/inventory/pages/Inventory'));
const InventoryImportExport = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryImportExport'));
const MissingInventoryList = React.lazy(() => import('../scenes/warehouses/inventory/pages/MissingInventoryList'));
const InventoryForPurchaseOrder = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryForPurchaseOrder'));

const NutritionHomepage = React.lazy(() => import('../scenes/nutrition/pages/NutritionHomepage'));
const ProductAnalysis = React.lazy(() => import('../scenes/products/analysis/ProductAnalysis'));
const FnbProductsNoMatch = React.lazy(() => import('../scenes/dataOperations/pages/FnbProductsNoMatch'));
const PurchaseOrdersList = React.lazy(() => import('../scenes/purchaseOrders/orders/pages/PurchaseOrdersList'));
const PurchaseOrderEdit = React.lazy(() => import('../scenes/purchaseOrders/orders/pages/PurchaseOrderEdit'));
const PurchaseOrderProductsSelection = React.lazy(() => import('../scenes/purchaseOrders/orders/pages/PurchaseOrderProductSelection'));
const PurchaseProductsCalculations = React.lazy(() => import('../scenes/purchaseOrders/orders/pages/PurchaseProductCalculations'));
const PurchaseOrderReserve = React.lazy(() => import('../scenes/purchaseOrders/orders/pages/orderReserve/PurchaseOrderReserve'));
const OpTest = React.lazy(() => import('../scenes/orders/orders/pages/OpTest'));
const FnbConsolidationList = React.lazy(() => import('../scenes/orders/orders/pages/FnbConsolidationList'));
const FnbStatus = React.lazy(() => import('../scenes/orders/orders/pages/FnbStatus'));
const FnbConsolidationDetails = React.lazy(() => import('../scenes/orders/orders/pages/FnbConsolidationDetails'));
const SettingsPage = React.lazy(() => import('../scenes/misc/pages/SettingsPage'));
const PrintConsolidationList = React.lazy(() => import('../scenes/orders/orders/pages/printConsollidation/PrintConsolidationList'));
const TempOperations = React.lazy(() => import('../scenes/products/imports/pages/TempOperations'));
const FnbHangingOrders = React.lazy(() => import('../scenes/dataOperations/pages/FnbHangingOrders'));
const FnbProblematicConsolidations = React.lazy(() => import('../scenes/dataOperations/pages/FnbProblematicConsolidations'));
const InventoryReports = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryReports'));
const InventoryReportsIncoming = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryReportsIncoming'));
const InventoryReportsOutgoing = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryReportsOutoging'));
const StorageConditionsList = React.lazy(() => import('../scenes/warehouses/storageConditions/StorageConditionsList'));
const StorageConditionsEdit = React.lazy(() => import('../scenes/warehouses/storageConditions/StorageConditionsEdit'));
const DeliveriesList = React.lazy(() => import('../scenes/orders/orders/pages/Deliveries/DeliveriesList'));
const DeliveryForm = React.lazy(() => import('../scenes/orders/orders/pages/Deliveries/DeliveryForm'));
const PalletDescriptionPrint = React.lazy(() => import('../scenes/orders/orders/pages/palletDescription/PalletDescriptionPrint'));
const UsersList = React.lazy(() => import('../scenes/users/pages/UsersList'));
const FnbInvoiceList = React.lazy(() => import('../scenes/orders/orders/pages/FnbInvoiceList'));
const FnbInvoiceEdit = React.lazy(() => import('../scenes/orders/orders/pages/FnbInvoiceEdit'));
const WarehouseOwnerList = React.lazy(() => import('../scenes/contractors/warehouseOwners/pages/WarehouseOwnerList'));
const WarehouseOwnerEdit = React.lazy(() => import('../scenes/contractors/warehouseOwners/pages/WarehouseOwnerEdit'));
const PrintLotReport = React.lazy(() => import('../scenes/orders/orders/pages/PrintLotReport'));
const ExternalOrders = React.lazy(() => import('../scenes/externalOrders/ExternalOrders'));
const MarginsList = React.lazy(() => import('../scenes/other/Margins/pages/MarginsList'));
const MarginEdit = React.lazy(() => import('../scenes/other/Margins/pages/MarginEdit'));

const SvilaClientsList = React.lazy(() => import('../scenes/svilaClients/pages/SvilaClientsList'));
const SvilaClientEdit = React.lazy(() => import('../scenes/svilaClients/pages/SvilaClientEdit'));
const InventoryForWarehouse = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryForWarehouse'));
const InventoryForGenericInWarehouse = React.lazy(() => import('../scenes/warehouses/inventory/pages/InventoryForGenericInWarehouse'));

const AppRoutes = () => (
  <Suspense fallback={<div>Loading...</div>}>
    <Switch>
      <Route exact path="/">
        <WelcomePage />
      </Route>
      <Route exact path="/login">
        <LoginPage />
      </Route>
      <Route exact path="/forgot">
        <ForgotPasswordPage />
      </Route>
      <Route exact path="/403">
        <Forbidden />
      </Route>
      <RestrictedRoute exact path="/home" component={<HomePage />} />

      <RestrictedRoute
        exact
        path="/brands"
        component={<BrandsList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/brands/create"
        component={<BrandCreate />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/brands/edit/:id"
        component={<BrandEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/ingredients"
        component={<IngredientsList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/ingredients/create"
        component={<IngredientCreate />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/ingredients/edit/:id"
        component={<IngredientEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/hscodes"
        component={<HsCodesList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/hscodes/create"
        component={<HsCodeCreate />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/hscodes/edit/:id"
        component={<HsCodeEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/products"
        component={<GenericProductsList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.OFFICE, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/products/create"
        component={<GenericProductEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.OFFICE, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/products/edit/:id"
        component={<GenericProductEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.OFFICE, ROLES.DATABASE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/categories"
        component={<CategoriesList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/categories/create"
        component={<CategoryCreate />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/categories/edit/:id"
        component={<CategoryEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/manufacturers"
        component={<ManufacturersList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE]}
      />
      <RestrictedRoute
        exact
        path="/manufacturers/create"
        component={<ManufacturerEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE]}
      />
      <RestrictedRoute
        exact
        path="/manufacturers/edit/:id"
        component={<ManufacturerEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE]}
      />

      <RestrictedRoute
        exact
        path="/suppliers"
        component={<SuppliersList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE, ROLES.SVILA_CLIENT_ADMIN, ROLES.SVILA_CLIENT]}
      />
      <RestrictedRoute
        exact
        path="/suppliers/create"
        component={<SupplierEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE, ROLES.SVILA_CLIENT_ADMIN, ROLES.SVILA_CLIENT]}
      />
      <RestrictedRoute
        exact
        path="/suppliers/edit/:id"
        component={<SupplierEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE, ROLES.SVILA_CLIENT_ADMIN, ROLES.SVILA_CLIENT]}
      />

      <RestrictedRoute
        exact
        path="/customers"
        component={<CustomersList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE, ROLES.WAREHOUSE_EXTERNAL_USER, ROLES.SVILA_CLIENT, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/customers/create"
        component={<CustomerEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE, ROLES.WAREHOUSE_EXTERNAL_USER, ROLES.SVILA_CLIENT, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/customers/edit/:id"
        component={<CustomerEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE, ROLES.SVILA_CLIENT, ROLES.SVILA_CLIENT_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/warehouse-owners"
        component={<WarehouseOwnerList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE, ROLES.SVILA_CLIENT, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/warehouse-owners/create"
        component={<WarehouseOwnerEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE, ROLES.SVILA_CLIENT, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/warehouse-owners/edit/:id"
        component={<WarehouseOwnerEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE, ROLES.SVILA_CLIENT, ROLES.SVILA_CLIENT_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/pallets"
        component={<PalletsList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/pallets/create"
        component={<PalletEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/pallets/edit/:id"
        component={<PalletEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/labels"
        component={<LabelsList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/labels/create"
        component={<LabelEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/labels/edit/:id"
        component={<LabelEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/containers"
        component={<ContainersList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/containers/create"
        component={<ContainerEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/containers/edit/:id"
        component={<ContainerEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/trucks"
        component={<TrucksList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/trucks/create"
        component={<TruckEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/trucks/edit/:id"
        component={<TruckEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/packages"
        component={<PackagesList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/packages/create"
        component={<PackageEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/packages/edit/:id"
        component={<PackageEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />

      <RestrictedRoute exact path="/users" component={<UsersList />} roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SVILA_CLIENT_ADMIN]} />
      <RestrictedRoute
        exact
        path="/users/create"
        component={<UserEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/users/edit/:id"
        component={<UserEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/settings"
        component={<SettingsPage />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/orders-list"
        component={<OrdersList />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.OFFICE,
          ROLES.SALES,
          ROLES.DATABASE_ADMIN,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />

      <RestrictedRoute
        exact
        path="/orders/fnb"
        component={<FnbOrdersList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/orders/fnb-invoices"
        component={<FnbInvoiceList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/orders/fnb-invoices/:id"
        component={<FnbInvoiceEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/orders/fnb/:orderId"
        component={<FnbOrder />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.FNB,
        ]}
      />
      <RestrictedRoute
        exact
        path="/orders/fnb-consolidations"
        component={<FnbConsolidationList />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.OFFICE,
          ROLES.SALES,
          ROLES.DATABASE_ADMIN,
        ]}
      />
      <RestrictedRoute
        exact
        path="/orders/fnb-consolidation-details/:orderId"
        component={<FnbConsolidationDetails />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.OFFICE,
          ROLES.SALES,
          ROLES.DATABASE_ADMIN,
        ]}
      />
      <RestrictedRoute
        exact
        path="/orders/print/:id"
        component={<PrintConsolidationList />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.OFFICE,
          ROLES.SALES,
          ROLES.DATABASE_ADMIN,
        ]}
      />
      <RestrictedRoute
        exact
        path="/orders/print-lot-report/:id"
        component={<PrintLotReport />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.OFFICE,
          ROLES.SALES,
          ROLES.DATABASE_ADMIN,
        ]}
      />
      <RestrictedRoute
        exact
        path="/orders/fnb-status"
        component={<FnbStatus />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.FNB]}
      />
      <RestrictedRoute
        exact
        path="/orders/create"
        component={<OrderEdit />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DATABASE_ADMIN,
          ROLES.SALES,
          ROLES.OFFICE,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT,
        ]}
      />

      <RestrictedRoute
        exact
        path="/orders/edit/:id"
        component={<OrderEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN, ROLES.OFFICE, ROLES.SALES, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/purchase-orders"
        component={<PurchaseOrdersList />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.OFFICE,
          ROLES.SALES,
          ROLES.DATABASE_ADMIN,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/purchase-orders/create"
        component={<PurchaseOrderEdit />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DATABASE_ADMIN,
          ROLES.SALES,
          ROLES.OFFICE,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/purchase-delivery/create/:id"
        component={<DeliveryForm />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DATABASE_ADMIN,
          ROLES.SALES,
          ROLES.OFFICE,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/purchase-orders/edit/:id"
        component={<PurchaseOrderEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.SVILA_CLIENT_ADMIN, ROLES.SVILA_CLIENT]}
      />
      <RestrictedRoute
        exact
        path="/purchase-delivery/edit/:id"
        component={<DeliveryForm />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DATABASE_ADMIN,
          ROLES.SALES,
          ROLES.OFFICE,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/purchase-delivery/:id"
        component={<DeliveriesList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN, ROLES.OFFICE, ROLES.SALES, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/optest/:id"
        component={<OpTest />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/purchase-orders/details/:id"
        component={<PurchaseOrderProductsSelection />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.SALES,
          ROLES.OFFICE,
          ROLES.DATABASE_ADMIN,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/purchase-orders/calculations/:id"
        component={<PurchaseProductsCalculations />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.OFFICE, ROLES.SALES, ROLES.WAREHOUSE_EXTERNAL_USER, ROLES.SVILA_CLIENT_ADMIN, ROLES.SVILA_CLIENT]}
      />
      <RestrictedRoute
        exact
        path="/purchase-orders/reserve/:id"
        component={<PurchaseOrderReserve />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.OFFICE, ROLES.SALES, ROLES.WAREHOUSE_EXTERNAL_USER]}
      />
      <RestrictedRoute
        exact
        path="/orders/details/:id"
        component={<OrderProductSelection />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.SALES,
          ROLES.OFFICE,
          ROLES.DATABASE_ADMIN,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/orders/details/:id/:consolidation"
        component={<OrderProductSelection />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SALES, ROLES.OFFICE, ROLES.DATABASE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/orders/calculations/:id"
        component={<OrderProductsCalculations />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.OFFICE,
          ROLES.SALES,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN
        ]}
      />

      <RestrictedRoute
        exact
        path="/orders/printout/:id"
        component={<OrderPrintout />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.SALES,
          ROLES.OFFICE,
          ROLES.WAREHOUSE_EXTERNAL_USER,
        ]}
      />

      <RestrictedRoute
        exact
        path="/orders/printout/:id/:consolidation"
        component={<OrderPrintout />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN, ROLES.SALES, ROLES.OFFICE]}
      />
      <RestrictedRoute
        exact
        path="/orders/pallet-description/:id"
        component={<PalletDescription />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.SALES,
          ROLES.OFFICE,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/orders/pallet-description/print/:id"
        component={<PalletDescriptionPrint />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.SALES,
          ROLES.OFFICE,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/orders/pallet-numbering/:id"
        component={<PalletNumbering />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.SALES,
          ROLES.OFFICE,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/orders/reserve/:id"
        component={<OrderReserve />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.OFFICE, ROLES.SALES,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT]}
      />
      <RestrictedRoute
        exact
        path="/orders/reserve/:id/:consolidation"
        component={<OrderReserve />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.OFFICE, ROLES.SALES]}
      />
      <RestrictedRoute
        exact
        path="/orders/status/:id"
        component={<Status />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.OFFICE,
          ROLES.SALES,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/orders/status/:id/:consolidation"
        component={<Status />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN, ROLES.OFFICE, ROLES.SALES]}
      />
      <RestrictedRoute
        exact
        path="/orders/proforma/:id"
        component={<Proforma />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.SALES,
          ROLES.OFFICE,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT]}
      />
      <RestrictedRoute
        exact
        path="/orders/invoice/:id"
        component={<Invoice />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.SALES,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.OFFICE,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/orders/invoice/:id/:consolidation"
        component={<Invoice />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SALES, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN, ROLES.OFFICE]}
      />
      <RestrictedRoute
        exact
        path="/data-operations"
        component={<DataOperations />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/products/imports/upload"
        component={<UploadForImport />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/products/import-sizes/upload"
        component={<UploadForImportSizes />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/products/import-supplier-data/upload"
        component={<UploadForImportSupplierData />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/products/imports/match"
        component={<MatchChocolateToGtin />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/products/imports/display-matches"
        component={<DisplayMatching />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/products/products/packings"
        component={<ProductPackingAnalysis />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/products/analysis"
        component={<ProductAnalysis />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/fnb/products-no-match"
        component={<FnbProductsNoMatch />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DATABASE_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.OFFICE,
        ]}
      />
      <RestrictedRoute
        exact
        path="/fnb/fnb-hanging"
        component={<FnbHangingOrders />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DATABASE_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.OFFICE,
        ]}
      />
      <RestrictedRoute
        exact
        path="/fnb/fnb-problematic-consolidations"
        component={<FnbProblematicConsolidations />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DATABASE_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.OFFICE,
        ]}
      />

      <RestrictedRoute
        exact
        path="/warehouses"
        component={<WarehousesList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/warehouses/create"
        component={<WarehouseEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/warehouses/edit/:id"
        component={<WarehouseEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT, ROLES.SVILA_CLIENT_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/transport-pricings"
        component={<TransportPricingList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE]}
      />
      <RestrictedRoute
        exact
        path="/transport-pricings/create"
        component={<TransportPricingEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE]}
      />
      <RestrictedRoute
        exact
        path="/transport-pricings/edit/:id"
        component={<TransportPricingEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE]}
      />

      <RestrictedRoute
        exact
        path="/margins"
        component={<MarginsList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE]}
      />
      <RestrictedRoute
        exact
        path="/margins/create"
        component={<MarginEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE]}
      />
      <RestrictedRoute
        exact
        path="/margins/edit/:id"
        component={<MarginEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE]}
      />

      {/* Inventory */}

      <RestrictedRoute
        exact
        path="/inventory/edit/:genericId"
        component={<InventoryEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/inventory/edit-remove"
        component={<InventoryRemoveEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/inventory/add/:genericId"
        component={<InventoryAdd />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT,]}
      />
      <RestrictedRoute
        exact
        path="/inventory/add-from-template/:genericId"
        component={<InventoryAddFromTemplate />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/inventory/adjustment/:genericId"
        component={<InventoryAdjustment />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/inventory/remove/:genericId"
        component={<InventoryRemove />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/inventory/list-for-generic/:id"
        component={<InventoryForGeneric />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.WAREHOUSE_EXTERNAL_USER,
        ]}
      />
      <RestrictedRoute
        exact
        path="/inventory/list-for-generic/in-warehouse/:warehouseId/:genericProductId"
        component={<InventoryForGenericInWarehouse />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.WAREHOUSE_EXTERNAL_USER,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT
        ]}
      />
      <RestrictedRoute
        exact
        path="/inventory"
        component={<Inventory />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.WAREHOUSE_EXTERNAL_USER,
        ]}
      />
      <RestrictedRoute
        exact
        path="/inventory/for-warehouse/:warehouseId"
        component={<InventoryForWarehouse />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT]}
      />
      <RestrictedRoute
        exact
        path="/inventory/split/:genericId"
        component={<InventorySplit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/inventory/select-product"
        component={<InventoryAddSelectProduct />}
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT]}
      />
      <RestrictedRoute
        exact
        path="/inventory/move"
        component={<InventoryMove />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/inventory/import-export"
        component={<InventoryImportExport />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/inventory/missing"
        component={<MissingInventoryList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.WAREHOUSE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/inventory/reports"
        component={<InventoryReports />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/inventory/reports/incoming"
        component={<InventoryReportsIncoming />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/inventory/reports/outgoing"
        component={<InventoryReportsOutgoing />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/inventory/purchase-order/:id"
        component={<InventoryForPurchaseOrder />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.WAREHOUSE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/inventory/migrate-remove"
        component={<TempOperations />}
        roles={[ROLES.SUPER_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/storage-conditions"
        component={<StorageConditionsList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.WAREHOUSE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/storage-conditions/create"
        component={<StorageConditionsEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.WAREHOUSE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/storage-conditions/edit/:id"
        component={<StorageConditionsEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.WAREHOUSE_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/svila-clients"
        component={<SvilaClientsList />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/svila-clients/create"
        component={<SvilaClientEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/svila-clients/edit/:id"
        component={<SvilaClientEdit />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
      />

      <RestrictedRoute
        exact
        path="/nutrition"
        component={<IngredientCreate />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/nutrition/ingredient/:ingredientId"
        component={<NutritionHomepage />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/nutrition/:productId"
        component={<NutritionHomepage />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />
      <RestrictedRoute
        exact
        path="/external-orders-list"
        component={<ExternalOrders />}
        roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN]}
      />

      {/*
    Not Found
      <Route component={NotFound}  roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]} />
      */}
    </Switch>
  </Suspense>
);
export default AppRoutes;
